import { hc } from 'hono/client';
import { nanoid } from 'nanoid';

import {
  type ClientToServerWebsocketEvent,
  type ServerToClientWebsocketEvent,
} from '@mai/types';

import { logger } from '@utils/logger';

let ws: WebSocket | null;

let topicHandlers: {
  subscriptionId: string;
  data: ClientToServerWebsocketEvent['data'];
  handler: (event: ServerToClientWebsocketEvent) => void;
}[] = [];

function startWebsocket() {
  const client = hc(
    import.meta.env.MODE === 'development'
      ? 'http://localhost:3003'
      : 'https://platform-api.us.catalyzed.ai',
  );
  ws = client.ws.$ws(0);
  ws.addEventListener('open', () => {
    logger.info({}, 'WebSocket connection opened');

    // Need to synchronize subscriptions with the server on open (aka reconnect or first connect race condition)
    topicHandlers.forEach((subscription) => {
      switch (subscription.data.topic) {
        case 'ping': {
          sendServerEvent({
            type: 'subscribe',
            data: { topic: 'ping', options: {} },
          });
          break;
        }
        case 'resource_updated': {
          const resourceType = subscription.data.options.resourceType;
          const resourceId = subscription.data.options.resourceId;
          sendServerEvent({
            type: 'subscribe',
            data: {
              topic: 'resource_updated',
              options: { resourceType, resourceId },
            },
          });
          break;
        }
        case 'conversation_message_stream_updated': {
          const conversationId = subscription.data.options.conversationId;
          sendServerEvent({
            type: 'subscribe',
            data: {
              topic: 'conversation_message_stream_updated',
              options: { conversationId },
            },
          });
          break;
        }
      }
    });
  });

  ws.addEventListener('message', (event) => {
    const data = JSON.parse(event.data) as ServerToClientWebsocketEvent;
    logger.info({ data }, 'Received WebSocket message');

    switch (data.type) {
      case 'ping': {
        topicHandlers
          .filter((subscription) => subscription.data.topic === 'ping')
          .forEach((subscription) => subscription.handler(data));
        break;
      }
      case 'resource_updated': {
        topicHandlers
          .filter(
            (subscription) =>
              subscription.data.topic === 'resource_updated' &&
              subscription.data.options.resourceType ===
                data.data.resourceType &&
              subscription.data.options.resourceId === data.data.resourceId,
          )
          .forEach((subscription) => subscription.handler(data));
        break;
      }
      case 'conversation_message_stream_updated': {
        topicHandlers
          .filter(
            (subscription) =>
              subscription.data.topic ===
                'conversation_message_stream_updated' &&
              subscription.data.options.conversationId ===
                data.data.conversationId,
          )
          .forEach((subscription) => subscription.handler(data));
        break;
      }
      case 'error': {
        logger.error({ error: data.data }, 'WebSocket error');
        break;
      }
      case 'subscription_confirmed': {
        logger.info({ data }, 'Subscription confirmed');
        break;
      }
    }
  });

  ws.addEventListener('error', (error) => {
    logger.error({ error }, 'WebSocket error');
  });
}

function sendServerEvent(event: ClientToServerWebsocketEvent) {
  logger.info({ event }, 'Sending WebSocket event');
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(event));
  }
}

export function useWebsocketPubSub() {
  function subscribe(
    subscription: ClientToServerWebsocketEvent['data'],
    handler: (event: ServerToClientWebsocketEvent) => void,
  ): string {
    if (!ws) {
      startWebsocket();
    }

    const subscriptionId = nanoid();

    topicHandlers.push({
      subscriptionId,
      data: subscription,
      handler,
    });

    sendServerEvent({
      type: 'subscribe',
      data: subscription,
    });

    return subscriptionId;
  }

  function unsubscribe(subscriptionId: string) {
    const subscription = topicHandlers.find(
      (handler) => handler.subscriptionId === subscriptionId,
    );
    if (!subscription) {
      return;
    }

    topicHandlers = topicHandlers.filter(
      (handler) => handler.subscriptionId !== subscriptionId,
    );

    sendServerEvent({
      type: 'unsubscribe',
      data: subscription.data,
    });
  }

  return {
    subscribe,
    unsubscribe,
  };
}
